import type { SoundAnalysisV2ListItem } from "@/lib/api";

interface SoundRelatedHistoryProps {
  currentId: number;
  elderlyName: string;
  records: SoundAnalysisV2ListItem[];
  getSoundTypeLabel: (record: SoundAnalysisV2ListItem) => string;
  getRiskMeta: (record: SoundAnalysisV2ListItem) => { label: string; tone: "success" | "warning" | "danger" | "neutral" };
  formatDateTime: (value: string | null) => string;
  formatConfidence: (value: number | null) => string;
}

const dotStyle = { success: "bg-emerald-500", warning: "bg-orange-500", danger: "bg-red-500", neutral: "bg-slate-400" };
const badgeStyle = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-orange-50 text-orange-700 border-orange-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-slate-50 text-slate-600 border-slate-200",
};

const SoundRelatedHistory = ({ currentId, elderlyName, records, getSoundTypeLabel, getRiskMeta, formatDateTime, formatConfidence }: SoundRelatedHistoryProps) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3.5">
      <div className="flex items-center gap-2"><i className="ri-history-line text-sm text-slate-500" /><h2 className="text-sm font-semibold text-slate-700">최근 소리 이력</h2></div>
      <span className="text-xs text-slate-400">{elderlyName} · 최근 {records.length}건</span>
    </div>
    <div className="divide-y divide-slate-100">
      {records.map((record) => {
        const risk = getRiskMeta(record);
        const isCurrent = record.soundAnalysisEventId === currentId;
        return (
          <div key={record.soundAnalysisEventId} className={`flex items-center gap-3 px-5 py-3 transition-colors ${isCurrent ? "border-l-2 border-blue-400 bg-blue-50/60" : "hover:bg-slate-50/80"}`}>
            <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${dotStyle[risk.tone]}`} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2"><span className="truncate text-sm font-medium text-slate-700">{getSoundTypeLabel(record)}</span>{isCurrent && <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">현재</span>}</div>
              <div className="mt-0.5 flex items-center gap-2"><span className="text-xs text-slate-400">{formatDateTime(record.analyzedAt)}</span><span className="text-slate-200">·</span><span className="text-xs text-slate-400">신뢰도 {formatConfidence(record.representativeConfidence)}</span></div>
            </div>
            <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${badgeStyle[risk.tone]}`}>{risk.label}</span>
          </div>
        );
      })}
    </div>
  </div>
);

export default SoundRelatedHistory;
